import { useMutation, useQuery } from '@tanstack/react-query';
import { db } from '@util/firebase';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import {
  getDocs,
  query,
  collection,
  where,
  updateDoc,
  doc,
} from 'firebase/firestore';
import { z } from 'zod';

export enum CANCELLATION_STATUS {
  CANCELLED = 'cancelled',
  PENDING = 'pending',
  REFUNDED = 'refunded',
  UPGRADED = 'upgraded',
}

const cancellationRequestSchema = z.object({
  amount_charged: z.number(),
  buyer_id: z.string(),
  chat_id: z.string(),
  created: z.number(),
  id: z.string(),
  order_id: z.string(),
  product_id: z.string(),
  reason: z.string(),
  requested_by: z.literal('buyer').or(z.literal('seller')).default('seller'),
  seller_id: z.string(),
  status: z.nativeEnum(CANCELLATION_STATUS),
});

export type CancellationRequest = z.infer<typeof cancellationRequestSchema>;

export const useCancellationRequests = ({
  status,
  order_id,
  seller_id,
  buyer_id,
}: {
  status?: CANCELLATION_STATUS;
  order_id?: string;
  seller_id?: string;
  buyer_id?: string;
} = {}) => {
  const { showErrorToast, showSuccessToast } = useToastContext();
  const { userDoc } = useAuth();
  const data = useQuery({
    queryKey: ['cancellation-requests', status, order_id, seller_id, buyer_id],
    queryFn: async () => {
      const q = query(
        collection(db, 'cancellation_requests'),
        ...(status ? [where('status', '==', status)] : []),
        ...(order_id ? [where('order_id', '==', order_id)] : []),
        ...(seller_id ? [where('seller_id', '==', seller_id)] : []),
        ...(buyer_id ? [where('buyer_id', '==', buyer_id)] : [])
      );
      const snap = await getDocs(q);
      return snap.docs.map((doc) => doc.data() as CancellationRequest);
    },
    enabled: !!userDoc?.roles?.admin || !!seller_id || !!buyer_id,
  });

  const updateCancellationRequest = useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: CANCELLATION_STATUS;
    }) => {
      const ref = doc(db, 'cancellation_requests', id);
      await updateDoc(ref, { status });
    },
    onSuccess: () => {
      data.refetch();
      showSuccessToast('Cancellation request updated');
    },
    onError: (error) => {
      console.error(error);
      showErrorToast('Error updating cancellation request: ' + error.message);
    },
  });

  return {
    data,
    updateCancellationRequest,
  };
};
